import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Plus, Heart, HeartPulse, Pill, Calendar, Bell, CheckCircle2,
  ArrowRight, Users, ChevronRight, Stethoscope, X,
} from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { useCareRecipientStore } from "@/store/careRecipientStore";
import { guardianApi } from "@/lib/api";
import { toast } from "sonner";

const BANNER = "https://static.prod-images.emergentagent.com/jobs/67e85589-df3b-4949-9f26-d13809d545ec/images/e78f176d12e9283b8c453aeb330100046fb3138c52b425eb7af7636efdf2e86d.jpeg";

const VITAL_METRICS = [
  { key: "bp", label: "Blood Pressure", fields: [{ k: "systolic", ph: "Systolic" }, { k: "diastolic", ph: "Diastolic" }] },
  { key: "glucose", label: "Blood Sugar", unit: "mg/dL", fields: [{ k: "value", ph: "mg/dL" }] },
  { key: "spo2", label: "SpO₂", unit: "%", fields: [{ k: "value", ph: "%" }] },
  { key: "weight", label: "Weight", unit: "kg", fields: [{ k: "value", ph: "kg" }] },
];

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return null; }
}

const TONE = {
  green: { color: "#16A34A", bg: "#DCFCE7" },
  blue: { color: "#2563EB", bg: "#DBEAFE" },
  amber: { color: "#D97706", bg: "#FEF3C7" },
};

export default function SeniorCareGuardian() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const { setRecipient } = useCareRecipientStore();
  const [loading, setLoading] = useState(true);
  const [parents, setParents] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [overallAdherence, setOverallAdherence] = useState(null);
  const [overallStatus, setOverallStatus] = useState("Stable");
  const [logOpen, setLogOpen] = useState(false);
  const [logMetric, setLogMetric] = useState("bp");
  const [logForm, setLogForm] = useState({});
  const [saving, setSaving] = useState(false);

  const uid = user?._id;

  const load = useCallback(async () => {
    if (!uid) { setLoading(false); return; }
    try {
      const { data } = await guardianApi.careSummary(uid);
      setParents((data?.parents || []).map((p) => ({
        id: p.id, name: p.name, relation: p.relation, age: p.age,
        status: p.status, tone: p.tone, medication: p.medication,
        vitals: p.vitals || {}, lastHomeVisit: p.lastHomeVisit, upcomingAppointments: p.upcomingAppointments ?? 0,
      })));
      setAlerts(data?.alerts || []);
      setOverallAdherence(data?.overallAdherence ?? null);
      setOverallStatus(data?.overallStatus || "Stable");
    } catch { setParents([]); setAlerts([]); }
    finally { setLoading(false); }
  }, [uid]);

  useEffect(() => { load(); }, [load]);

  const primary = parents[0];
  const activeMetric = VITAL_METRICS.find((m) => m.key === logMetric);
  const openLog = () => { setLogMetric("bp"); setLogForm({}); setLogOpen(true); };

  const saveVital = async () => {
    if (!primary || !activeMetric) return;
    const nums = activeMetric.fields.map((f) => logForm[f.k]);
    if (nums.some((n) => n == null || n === "" || isNaN(Number(n)))) {
      toast.error("Please enter a valid number");
      return;
    }
    const value = logMetric === "bp"
      ? { systolic: Number(logForm.systolic), diastolic: Number(logForm.diastolic) }
      : { value: Number(logForm.value), ...(activeMetric.unit ? { unit: activeMetric.unit } : {}) };
    try {
      setSaving(true);
      await guardianApi.logDependentVital(primary.id, { metric: logMetric, value });
      toast.success("Vital logged");
      setLogOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const openParent = (p) => { setRecipient(p); navigate("/senior-care/services"); };
  const manageMeds = (p) => navigate(`/medications?patient=${p.id}&patientName=${encodeURIComponent(p.name)}`);

  if (loading) return <div className="flex justify-center py-20"><div className="h-8 w-8 rounded-full border-2 border-blue-600 border-t-transparent animate-spin" /></div>;

  const warn = overallStatus === "Needs attention";

  return (
    <div className="space-y-5 max-w-2xl mx-auto">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(-1)} data-testid="guardian-back" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <button onClick={() => navigate("/senior-care/services")} data-testid="guardian-book-care" className="flex items-center gap-1.5 rounded-full px-4 py-2 text-white text-sm font-bold" style={{ backgroundColor: "#2563EB" }}>
          <Plus className="h-4 w-4" /> Book Care
        </button>
      </div>

      <div>
        <h1 className="text-2xl font-extrabold" style={{ color: "#1E3A8A" }}>{parents.length ? "Mom & Dad's Care" : "Parents' Care"}</h1>
        <p className="text-muted-foreground">You&apos;re their Care Guardian</p>
      </div>

      {/* Banner */}
      <div className="flex items-center rounded-2xl p-4 overflow-hidden" style={{ backgroundColor: "#E0EAFF" }}>
        <div className="flex-1">
          <p className="text-lg font-extrabold leading-snug" style={{ color: "#1E3A8A" }}>You&apos;re far away.<br />Their care is still close.</p>
          <p className="mt-2 text-xs text-slate-600">Real-time updates, complete visibility and peace of mind.</p>
        </div>
        <img src={BANNER} alt="Care" className="h-24 w-24 rounded-xl object-cover ml-3" />
      </div>

      {/* Parent chips / empty state */}
      {parents.length === 0 ? (
        <button onClick={() => navigate("/profile")} data-testid="guardian-add-parent" className="w-full flex items-center gap-3 rounded-2xl p-4 text-left" style={{ backgroundColor: "#fff", border: "1.5px dashed #BFDBFE" }}>
          <Plus className="h-5 w-5" style={{ color: "#2563EB" }} />
          <div className="flex-1">
            <p className="font-bold" style={{ color: "#1E3A8A" }}>Add a parent</p>
            <p className="text-xs text-muted-foreground">Link your mother/father to manage their care &amp; bookings.</p>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </button>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {parents.slice(0, 2).map((p) => {
            const st = TONE[p.tone || "blue"] || TONE.blue;
            return (
              <button key={p.id} onClick={() => openParent(p)} data-testid={`guardian-parent-${p.id}`} className="flex items-center gap-2.5 rounded-2xl border border-border bg-card p-3 text-left hover:shadow-md transition-all">
                <div className="h-10 w-10 rounded-full flex items-center justify-center font-bold" style={{ backgroundColor: "#DBEAFE", color: "#2563EB" }}>{p.name?.charAt(0)?.toUpperCase()}</div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate">{p.name}</p>
                  <span className="inline-flex items-center gap-1.5 rounded-lg px-2 py-0.5 text-[11px] font-bold mt-0.5" style={{ backgroundColor: st.bg, color: st.color }}>
                    <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: st.color }} />{p.status || "Stable"}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Overall status */}
      <div className="flex items-center gap-4 rounded-2xl p-4" style={{ backgroundColor: warn ? "#FFFBEB" : "#F0FDF4", border: `1px solid ${warn ? "#FDE68A" : "#BBF7D0"}` }}>
        <div className="h-13 w-13 rounded-full flex items-center justify-center p-3" style={{ backgroundColor: warn ? "#FEF3C7" : "#DCFCE7" }}>
          <Heart className="h-6 w-6" style={{ color: warn ? "#D97706" : "#16A34A" }} />
        </div>
        <div className="flex-1">
          <p className="text-xs text-muted-foreground font-medium">Overall Care Status</p>
          <p className="text-xl font-extrabold" style={{ color: warn ? "#D97706" : "#16A34A" }}>{parents.length ? overallStatus : "Stable"}</p>
          <p className="text-xs text-slate-600 mt-0.5">
            {!parents.length ? "Add a parent to start tracking their care plan."
              : warn ? "Some doses were missed — tap an alert below to review."
              : overallAdherence != null ? `Care plan followed · ${overallAdherence}% medication adherence this week.`
              : "Following their care plan."}
          </p>
        </div>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl border border-border bg-card p-4">
          <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5"><Stethoscope className="h-3.5 w-3.5" /> Latest Home Visit</p>
          {primary && fmtDate(primary.lastHomeVisit) ? (
            <>
              <p className="text-lg font-extrabold mt-1.5" style={{ color: "#1E3A8A" }}>{fmtDate(primary.lastHomeVisit)}</p>
              <p className="text-xs font-bold" style={{ color: "#16A34A" }}>Completed</p>
            </>
          ) : (
            <>
              <p className="text-lg font-extrabold mt-1.5" style={{ color: "#1E3A8A" }}>—</p>
              <p className="text-xs font-bold" style={{ color: "#94A3B8" }}>No visits yet</p>
            </>
          )}
        </div>
        <button onClick={() => parents[0] && manageMeds(parents[0])} data-testid="guardian-adherence-card" className="rounded-2xl border border-border bg-card p-4 text-left hover:shadow-md transition-all">
          <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5"><Pill className="h-3.5 w-3.5" /> Medication Adherence</p>
          <p className="text-lg font-extrabold mt-1.5" style={{ color: "#1E3A8A" }}>{overallAdherence != null ? `${overallAdherence}%` : "—"}</p>
          <p className="text-xs font-bold" style={{ color: overallAdherence == null ? "#94A3B8" : overallAdherence >= 80 ? "#16A34A" : overallAdherence >= 50 ? "#2563EB" : "#D97706" }}>
            {overallAdherence == null ? "No data yet" : overallAdherence >= 80 ? "Good" : overallAdherence >= 50 ? "Fair" : "Low · tap to manage"}
          </p>
        </button>
        <div className="rounded-2xl border border-border bg-card p-4">
          <div className="flex items-center justify-between">
            <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5"><HeartPulse className="h-3.5 w-3.5" /> Latest Vitals {primary ? `(${primary.name.split(" ")[0]})` : ""}</p>
            {primary ? (
              <button onClick={openLog} data-testid="guardian-log-vitals" className="flex items-center gap-0.5 rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ backgroundColor: "#DBEAFE", color: "#2563EB" }}>
                <Plus className="h-3 w-3" /> Log
              </button>
            ) : null}
          </div>
          {primary && (primary.vitals?.bp || primary.vitals?.glucose || primary.vitals?.spo2) ? (
            <>
              <p className="text-sm font-bold mt-1.5 text-slate-700">
                {primary.vitals?.bp ? `BP ${primary.vitals.bp.value}` : ""}
                {primary.vitals?.bp && primary.vitals?.glucose ? " | " : ""}
                {primary.vitals?.glucose ? `Sugar ${primary.vitals.glucose.value}` : ""}
              </p>
              {primary.vitals?.spo2 ? <p className="text-sm font-bold text-slate-700">SpO₂ {primary.vitals.spo2.value}%</p> : null}
            </>
          ) : (
            <button onClick={openLog} data-testid="guardian-vitals-empty" className="text-left mt-1.5">
              <p className="text-sm font-bold" style={{ color: "#94A3B8" }}>No readings yet</p>
              <p className="text-xs font-bold" style={{ color: "#2563EB" }}>Tap “Log” to add ›</p>
            </button>
          )}
        </div>
        <button onClick={() => navigate("/at-home-care/my-bookings")} className="rounded-2xl border border-border bg-card p-4 text-left hover:shadow-md transition-all">
          <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> Upcoming Appointments</p>
          <p className="text-lg font-extrabold mt-1.5" style={{ color: "#1E3A8A" }}>{primary?.upcomingAppointments ? `${primary.upcomingAppointments}` : "0"}</p>
          <p className="text-xs font-bold" style={{ color: "#2563EB" }}>{primary?.upcomingAppointments ? "View all ›" : "None scheduled"}</p>
        </button>
      </div>

      {/* Alerts */}
      {alerts.length > 0 ? (
        <button
          onClick={() => { const a = alerts[0]; const p = parents.find((x) => x.id === a.parentId) || parents[0]; if (p) manageMeds(p); }}
          data-testid="guardian-alert"
          className="w-full flex items-center gap-3 rounded-2xl p-4 text-left"
          style={{ backgroundColor: "#FEE2E2" }}
        >
          <Bell className="h-5 w-5" style={{ color: "#DC2626" }} />
          <div className="flex-1">
            <p className="font-extrabold" style={{ color: "#B91C1C" }}>{alerts.length} alert{alerts.length > 1 ? "s" : ""} need{alerts.length > 1 ? "" : "s"} attention</p>
            <p className="text-xs" style={{ color: "#991B1B" }}>{alerts[0].message}</p>
          </div>
          <ChevronRight className="h-4 w-4" style={{ color: "#DC2626" }} />
        </button>
      ) : parents.length > 0 ? (
        <div className="flex items-center gap-2.5 rounded-2xl p-4" style={{ backgroundColor: "#F0FDF4", border: "1px solid #BBF7D0" }} data-testid="guardian-no-alert">
          <CheckCircle2 className="h-5 w-5" style={{ color: "#16A34A" }} />
          <p className="text-sm font-bold" style={{ color: "#15803D" }}>All good — no medication alerts right now.</p>
        </div>
      ) : null}

      {/* Full report */}
      <button onClick={() => navigate("/records")} data-testid="guardian-full-report" className="w-full flex items-center justify-center gap-2 rounded-2xl py-4 text-white font-extrabold" style={{ backgroundColor: "#2563EB" }}>
        View full health report <ArrowRight className="h-5 w-5" />
      </button>

      {/* Consent */}
      <button onClick={() => navigate("/consent")} data-testid="guardian-consent" className="w-full flex items-center gap-3 rounded-2xl border border-border bg-card p-4 text-left hover:shadow-md transition-all">
        <div className="h-11 w-11 rounded-full flex items-center justify-center" style={{ backgroundColor: "#EFF6FF" }}><Users className="h-5 w-5" style={{ color: "#2563EB" }} /></div>
        <div className="flex-1">
          <p className="font-bold">Consent &amp; Sharing</p>
          <p className="text-xs text-muted-foreground">Access is shared with your consent. You can manage who sees what.</p>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </button>

      {/* Log vitals modal */}
      {logOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={() => setLogOpen(false)} data-testid="guardian-log-modal">
          <div className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl bg-card p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-extrabold" style={{ color: "#1E3A8A" }}>Log vital{primary ? ` · ${primary.name.split(" ")[0]}` : ""}</h3>
              <button onClick={() => setLogOpen(false)} data-testid="guardian-log-close" className="text-muted-foreground"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex flex-wrap gap-2">
              {VITAL_METRICS.map((m) => (
                <button key={m.key} onClick={() => { setLogMetric(m.key); setLogForm({}); }} data-testid={`guardian-metric-${m.key}`}
                  className="rounded-full px-3.5 py-1.5 text-xs font-bold border transition-colors"
                  style={logMetric === m.key ? { backgroundColor: "#2563EB", color: "#fff", borderColor: "#2563EB" } : { backgroundColor: "transparent", color: "#475569", borderColor: "#E2E8F0" }}>
                  {m.label}
                </button>
              ))}
            </div>
            <div className="flex gap-3">
              {activeMetric.fields.map((f) => (
                <input key={f.k} type="number" inputMode="numeric" placeholder={f.ph} data-testid={`guardian-vital-input-${f.k}`}
                  value={logForm[f.k] ?? ""} onChange={(e) => setLogForm((s) => ({ ...s, [f.k]: e.target.value }))}
                  className="flex-1 rounded-xl border border-border bg-background px-4 py-3 text-base outline-none focus:border-blue-400" />
              ))}
            </div>
            <button onClick={saveVital} disabled={saving} data-testid="guardian-log-save"
              className="w-full rounded-xl py-3 text-white font-bold disabled:opacity-60" style={{ backgroundColor: "#2563EB" }}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
